import React, { ReactFragment } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { RoutePath } from 'enums/Routes';
import { formatNumber } from 'utils/formatNumber';
import useCurrentFlow from 'hooks/useCurrentFlow';
import { getApplicationData } from 'selectors/getApplicationData';
import { selectYourGoal } from 'selectors/yourGoal';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { getCreditScoreHistoryState } from 'selectors/getCreditScoreHistory';
import { AICoachGoalDecisionType, AICoachGoalPriority } from 'enums/AICoach';
import { DisclosureMark } from 'constants/disclosures';

import styles from './Disclaimers.module.scss';

interface Disclaimer {
  content: ReactFragment;
  customClassName?: string;
}

interface GetDisclaimersProps {
  isCardFlow: boolean;
  promoDisclaimerContent?: string;
  interestRate?: number;
  apr?: number;
  aiCoachGoals?: AICoachGoalItem[];
  creditScore?: number;
}

export const AI_COACH_DISCLAIMER = 'This guides your goals. It’s not financial advice.';

const hasPrimaryImproveCreditScoreGoal = (aiCoachGoals?: AICoachGoalItem[]) =>
  aiCoachGoals &&
  aiCoachGoals.some(
    (goal) =>
      goal.decisionType === AICoachGoalDecisionType.ImproveCreditScore && goal.priority === AICoachGoalPriority.Primary,
  );

const getDisclaimers = (
  path: RoutePath,
  { isCardFlow, promoDisclaimerContent, interestRate, apr, aiCoachGoals, creditScore }: GetDisclaimersProps,
): Disclaimer[] => {
  const disclaimers: Disclaimer[] = [];

  const generalDisclaimerRoutes = [RoutePath.OfferAvailable, RoutePath.SelectDebts, RoutePath.SmsLogin];
  const repaymentMethodRoutes = [RoutePath.RepaymentMethod, RoutePath.OpenDDA];

  const showUsPatriotActDisclosure =
    (path === RoutePath.YourName && !isCardFlow) || path === RoutePath.ConfirmYourDetails;

  if (generalDisclaimerRoutes.includes(path)) {
    const generalDisclaimerContent = {
      content: (
        <>
          <p className={styles.heading}>Plannery Lending Disclosures</p>
          <p>
            Not all applicants will qualify. Loans are subject to approval and verification of credit and employment
            information. Rates and terms are subject to change without notice. Loan amounts range from $1,050 to
            $21,000, with repayment terms from 12 to 60 months. Annual Percentage Rates (APRs) range from 13.13% to
            30.61%, as of June 15, 2026, based on creditworthiness and other factors. State minimum lending laws may
            apply. Loan minimums vary by state. Applications are for loans offered, made by, decisioned and owned by
            FinWise Bank, a Utah chartered bank.
          </p>
          <p>
            <span className={styles.disclosureTitle}>
              Time and Money Saved for individual customer {DisclosureMark.IndividualSavings}
            </span>{' '}
            Estimated savings are based on a comparison of customers existing obligations and assumes acceptance of
            their Plannery loan offer, along with consistent, on-time payments. Savings are not guaranteed and may vary
            based on individual repayment behavior.
          </p>
          {promoDisclaimerContent && (
            // eslint-disable-next-line react/no-danger
            <p dangerouslySetInnerHTML={{ __html: promoDisclaimerContent }} />
          )}
        </>
      ),
      customClassName: styles.generalDisclaimer,
    };

    return [generalDisclaimerContent];
  }

  if (showUsPatriotActDisclosure) {
    disclaimers.push({
      content: (
        <div>
          <p>
            <b>US Patriot Act Disclosure</b>
          </p>
          <p>
            To help the government fight the funding of terrorism and money laundering activities, federal law requires
            all financial institutions to obtain, verify, and record information that identifies each person who applies
            for an account. What this means for you: When you open an account, we may ask for your name, address, date
            of birth, and other information that will allow us to identify you. We may also ask to see your driver's
            license or other identifying documents.
          </p>
        </div>
      ),
      customClassName: styles.centeredText,
    });
  }

  if (repaymentMethodRoutes.includes(path)) {
    const bankDisclaimer = {
      content:
        '*Banking services are provided by FinWise Bank, Member FDIC. Your transactions will be processed through an FBO account held by FinWise Bank for the benefit of our customers. Your deposit balances held at FinWise Bank are insured by the Federal Deposit Insurance Corporation (FDIC).',
    };
    const lendingTreeDisclaimer = {
      content:
        '**Based on Q1 2025 LendingTree data for personal loans by credit band. Comparison reflects Plannery’s lowest offered APR by credit tiers versus average APRs on LendingTree’s platform for similar tiers. Actual rates vary by applicant profile and are not guaranteed.',
    };

    if (path === RoutePath.RepaymentMethod) {
      disclaimers.push(bankDisclaimer);
      disclaimers.push(lendingTreeDisclaimer);
    }
    if (path === RoutePath.OpenDDA) {
      disclaimers.push(bankDisclaimer);
    }
  }

  if (path === RoutePath.ConfirmLoan && interestRate && apr) {
    disclaimers.push({
      content: (
        <>
          *Interest rate of {formatNumber(interestRate, true)}% ({formatNumber(apr, true)}% Annual Percentage Rate).
          <br />
          APRs are based on creditworthiness and other factors. Not all applicants will qualify for the lowest rate.
        </>
      ),
      customClassName: styles.textMobileLeftDesktopCenter,
    });
  }

  if (hasPrimaryImproveCreditScoreGoal(aiCoachGoals) || creditScore) {
    disclaimers.push({
      content:
        '*The credit score shown is your Equifax VantageScore® 3.0, provided solely by Equifax. It is not a score from any other credit bureau and may differ from scores used by lenders. Any projected score changes or recommendations are based on Equifax modeling. Actual results may vary, as your credit score is influenced by many factors that can change over time. This feature is intended to serve as a guidance tool only, not a guarantee or final determination of your credit standing.',
    });
  }

  if (path === RoutePath.FinancialDashboard) {
    disclaimers.push({
      content:
        '**APRs are representative. Always check your current APR and terms with your lender or card issuer before making decisions about your accounts. See disclosure for more details.',
    });
  }

  return disclaimers;
};

const Disclaimers = () => {
  const location = useLocation();
  const { isCardFlow } = useCurrentFlow();
  const { application, apr } = useSelector(getApplicationData);
  const { aiCoachGoals } = useSelector(selectYourGoal);
  const creditScoreHistory = useSelector(getCreditScoreHistoryState);
  const { promo, interestRate } = application || {};

  const allDisclaimers = getDisclaimers(location.pathname as RoutePath, {
    isCardFlow,
    promoDisclaimerContent: promo?.disclaimerContent,
    interestRate,
    apr,
    aiCoachGoals,
    creditScore: creditScoreHistory?.[0]?.score,
  });

  if (allDisclaimers.length === 0) {
    return null;
  }

  return (
    <>
      {allDisclaimers.map((disclaimer, index) => (
        <div key={index} className={clsx(styles.commonDisclaimer, disclaimer.customClassName)}>
          {disclaimer.content}
        </div>
      ))}
    </>
  );
};

export default Disclaimers;
